import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { HandleParamChangeProps, HandleParamChangeType } from '@/layers/presentation/hooks';
import {
  createPatient,
  fetchPatientById,
  getPatientParamBySlug,
  updatePatient,
} from '@/layers/presentation/services/patients';
import logger from '@/layers/support/monitoring/logger';
import { NotificationType } from '@/types/modelEnums';
import { PatientExtended, PatientParamValues, UserPersonalInfo } from '@/types/models';
import { NewPatientRequest } from '@/types/patients';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useThrowNotification } from '../useThrowNotification';

const LOG_MODULE = '[Hooks / usePatientDetails]';

export function usePatientDetails(patientId: string, programId: string | null) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const throwNotification = useThrowNotification();
  const [selectedOptions, setSelectedOptions] = useState<PatientParamValues>({});
  const [patientPersonalInfo, setPatientPersonalInfo] = useState<UserPersonalInfo>({});
  const [patient, setPatient] = useState<PatientExtended | null>(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState<boolean>(true);
  const { profile, patientParamsDictionary, isLoadingParams } = useGlobalState();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEditEnabled, setIsEditEnabled] = useState<boolean>(false);

  const isAddMode = useMemo(() => patientId === 'add', [patientId]);

  useEffect(() => {
    const paramsString = searchParams.get('params');
    const allowedSlugs = ['c3_cancer_type', 'c3_cancer_subtype'];

    logger.debug(`${LOG_MODULE} useEffect`, {
      programId,
      searchParams,
      patientId,
      isAddMode,
      isLoadingParams,
      isLoadingPatient,
    });

    const fetchData = async () => {
      logger.debug(`${LOG_MODULE} fetchData`, { isLoadingPatient });

      try {
        // Edit patient mode
        if (patientId && !isAddMode) {
          logger.debug(`${LOG_MODULE} selectedOptions: patient profile mode`);

          const fetchedPatient = await fetchPatientById(patientId as string);
          setSelectedOptions(fetchedPatient.params);
          setPatient(fetchedPatient);

          if (fetchedPatient.personal_info) {
            setPatientPersonalInfo(fetchedPatient.personal_info);
          }

          // Create patient mode
        } else if (isAddMode) {
          logger.debug(`${LOG_MODULE} selectedOptions: patient add mode`, {
            paramsString,
            patientParamsDictionary,
          });

          if (paramsString) {
            const urlParams = JSON.parse(decodeURIComponent(paramsString));
            const filteredParams: PatientParamValues = {};

            allowedSlugs.forEach(slug => {
              const param = getPatientParamBySlug(patientParamsDictionary, slug);
              logger.debug(`${LOG_MODULE} allowedSlugs.forEach`, { slug, param });
              if (param && urlParams[param.id]) {
                filteredParams[param.id] = urlParams[param.id];
              }
            });

            logger.debug(`${LOG_MODULE} selectedOptions: patient add mode`, {
              urlParams,
              filteredParams,
            });
            setSelectedOptions(filteredParams);
          }
        }
      } catch (error) {
        logger.error(`${LOG_MODULE} setSelectedOptions`, { error });
        setError((error as Error).message);
      } finally {
        setIsLoadingPatient(false);
      }
    };
    fetchData();
  }, [
    searchParams,
    patientId,
    programId,
    isAddMode,
    isLoadingParams,
    isLoadingPatient,
    patientParamsDictionary,
  ]);

  const handleParamChange: HandleParamChangeType = (props: HandleParamChangeProps) => {
    logger.debug(`${LOG_MODULE} handleParamChange`, { props });
    setSelectedOptions(prev => ({ ...prev, [props.paramId]: props.paramValue }));
  };

  const handlePersonalInfoChange = (field: keyof UserPersonalInfo, value: string) => {
    logger.debug(`${LOG_MODULE} handlePersonalInfoChange`, { field, value });
    setPatientPersonalInfo(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmit = async () => {
    try {
      logger.debug(`${LOG_MODULE} handleSubmit`, {
        programId,
        patientId,
        isAddMode,
        profile,
        patientPersonalInfo,
        selectedOptions,
      });
      setIsSubmitting(true);

      // Create patient mode
      if (isAddMode && profile && profile.id) {
        const newPatient: NewPatientRequest = {
          provider_id: profile.id,
          personal_info: patientPersonalInfo,
          params: selectedOptions,
        };
        const createdPatient = await createPatient(newPatient);
        throwNotification(NotificationType.Success, 'Patient added successfully');

        if (programId) {
          router.push(`/provider/programs/${programId}/request/${createdPatient.id}`);
        } else {
          router.push(`/provider/patients/${createdPatient.id}`);
        }

        // Edit patient mode
      } else {
        await updatePatient(patientId as string, {
          personal_info: patientPersonalInfo,
          params: selectedOptions,
        });
        setIsEditEnabled(false);
        throwNotification(NotificationType.Success, 'Patient updated successfully');
      }

      setIsSubmitting(false);
    } catch (error) {
      logger.error(`${LOG_MODULE} handleSubmit`, { error });
      setError((error as Error).message);
    }
  };

  return {
    isAddMode,
    patient,
    isLoadingPatient,
    selectedOptions,
    setSelectedOptions,
    patientPersonalInfo,
    setPatientPersonalInfo,
    handleParamChange,
    handlePersonalInfoChange,
    handleSubmit,
    isSubmitting,
    isEditEnabled,
    setIsEditEnabled,
    error,
  };
}
